import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import {
  CircleNotch as CircleNotchIcon,
  FileArrowUp as FileArrowUpIcon,
} from '@phosphor-icons/react';
import type { PreviewPriceListUploadResponse } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import { WorkbookColumnPicker } from 'src/components/documents/WorkbookColumnPicker';
import {
  repreviewWorkbookSheet,
  suggestedColumnValue,
} from 'src/components/documents/workbookSheetPreview';
import { WorkbookSheetPicker } from 'src/components/documents/WorkbookSheetPicker';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import { usePreviewPriceListUploadV2 } from '@/api/rpc/hooks/accounts-receivable/useDiscountGroupsV2';

/** How the uploaded sheet's columns are identified. */
export type PriceListMatchMode = 'auto' | 'columns';

/** Everything the New Group dialog needs to submit an uploaded list. */
export interface PriceListUploadState {
  file: File | null;
  documentId: string | null;
  preview: PreviewPriceListUploadResponse | null;
  /** Worksheet the preview was read from; null until previewed. */
  sheetName: string | null;
  matchMode: PriceListMatchMode;
  /** Stringified header indexes; '' = not picked. */
  skuColumn: string;
  priceColumn: string;
}

export const EMPTY_PRICE_LIST_UPLOAD: PriceListUploadState = {
  file: null,
  documentId: null,
  preview: null,
  sheetName: null,
  matchMode: 'auto',
  skuColumn: '',
  priceColumn: '',
};

/** True when the state describes a submittable upload. */
export function isPriceListUploadReady(state: PriceListUploadState): boolean {
  if (!state.documentId || !state.preview) return false;
  // A sheet was picked but its preview hasn't landed yet — the column picks
  // (and the AI path's sheet) would describe the previous worksheet.
  if (state.sheetName !== state.preview.sheetName) return false;
  if (state.matchMode === 'auto') return true;
  return (
    state.skuColumn !== '' &&
    state.priceColumn !== '' &&
    state.skuColumn !== state.priceColumn
  );
}

/** Wire shape of the picked columns, or null in auto-match mode. */
export function priceListColumnMapping(
  state: PriceListUploadState,
): { skuColumnIndex: number; priceColumnIndex: number } | null {
  if (state.matchMode !== 'columns') return null;
  return {
    skuColumnIndex: Number(state.skuColumn),
    priceColumnIndex: Number(state.priceColumn),
  };
}

/** Adopt a preview (first load or a sheet change) and its column hints. */
function withPreview(
  state: PriceListUploadState,
  previewResult: PreviewPriceListUploadResponse,
): PriceListUploadState {
  return {
    ...state,
    preview: previewResult,
    sheetName: previewResult.sheetName,
    skuColumn: suggestedColumnValue(previewResult.suggestedItemNoColumn),
    priceColumn: suggestedColumnValue(previewResult.suggestedPriceColumn),
  };
}

/**
 * The "Upload list" body of the New Discount Group dialog: a spreadsheet
 * dropzone, then a match-mode toggle — Auto-match (the AI parser detects the
 * SKU + price columns) or Pick columns (explicit header picker with a sample
 * preview, like the exception price list upload). The file is stored as a
 * document as soon as it's dropped so the preview can be read server-side;
 * the parent submits the group with the header fields it already owns.
 */
export function UploadPriceListPanel({
  state,
  onChange,
  disabled,
}: {
  state: PriceListUploadState;
  onChange: (next: PriceListUploadState) => void;
  disabled?: boolean;
}) {
  const t = useGT();
  const upload = useUploadEntityDocument();
  const preview = usePreviewPriceListUploadV2();
  const busy = upload.isPending || preview.isPending;

  async function selectFile(selected: File | null) {
    if (!selected) return;
    onChange({
      ...EMPTY_PRICE_LIST_UPLOAD,
      matchMode: state.matchMode,
      file: selected,
    });
    try {
      // Pre-entity upload: the group doesn't exist yet, so anchor the initial
      // association on a placeholder id; the backend re-homes the document
      // onto the created group.
      const { documentId } = await upload.mutateAsync({
        entityType: 'ar_discount_group',
        entityId: crypto.randomUUID(),
        file: selected,
      });
      const previewResult = await preview.mutateAsync({ documentId });
      onChange(
        withPreview({ ...state, file: selected, documentId }, previewResult),
      );
    } catch (err) {
      onChange({ ...EMPTY_PRICE_LIST_UPLOAD, matchMode: state.matchMode });
      toast.error(
        err instanceof Error
          ? err.message
          : t('Could not read the file', {
              context: 'price list upload error toast',
            }),
      );
    }
  }

  /**
   * Re-preview the uploaded workbook from another worksheet. The pick is
   * recorded immediately so the parent's submit guard (isPriceListUploadReady)
   * holds until the matching preview arrives; the other controls are disabled
   * while `busy` so the captured `state` can't go stale meanwhile.
   */
  function selectSheet(sheetName: string) {
    if (!state.documentId || sheetName === state.sheetName) return;
    const picked = { ...state, sheetName };
    onChange(picked);
    return repreviewWorkbookSheet({
      documentId: state.documentId,
      currentSheetName: state.sheetName,
      nextSheetName: sheetName,
      preview: preview.mutateAsync,
      onPreview: (result) => onChange(withPreview(picked, result)),
      onError: () =>
        onChange({ ...state, sheetName: state.preview?.sheetName ?? null }),
      fallbackMessage: t('Could not read the sheet', {
        context: 'price list upload error toast',
      }),
    });
  }

  const modes: Array<{ value: PriceListMatchMode; label: React.ReactNode }> = [
    {
      value: 'auto',
      label: <T context="price list upload column mode">Auto-match</T>,
    },
    {
      value: 'columns',
      label: <T context="price list upload column mode">Pick columns</T>,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="text-sm font-medium">
          <T context="upload price list spreadsheet section">Spreadsheet</T>{' '}
          <span className="font-normal text-muted-foreground">
            <T context="accepted spreadsheet file formats">
              (.csv, .xlsx, .xls)
            </T>
          </span>
        </div>
        {state.file ? (
          <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
            <FileArrowUpIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate font-medium">
              {state.file.name}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-7"
              disabled={busy || disabled}
              onClick={() =>
                onChange({
                  ...EMPTY_PRICE_LIST_UPLOAD,
                  matchMode: state.matchMode,
                })
              }
            >
              <T context="replace the selected spreadsheet">Replace file</T>
            </Button>
          </div>
        ) : (
          <FileUploadDropzone
            onFilesSelected={(files) => void selectFile(files[0] ?? null)}
            uploadProgress={[]}
            isUploading={false}
            maxFiles={1}
            accept=".csv,.xlsx,.xls"
          />
        )}
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleNotchIcon className="h-4 w-4 animate-spin" />
          <T context="price list upload progress">
            Uploading and reading the spreadsheet…
          </T>
        </div>
      )}

      {state.preview && state.preview.sheetNames.length > 1 && (
        <WorkbookSheetPicker
          sheetNames={state.preview.sheetNames}
          value={state.sheetName ?? state.preview.sheetName}
          onChange={(sheetName) => void selectSheet(sheetName)}
          disabled={busy || disabled}
        />
      )}

      {state.preview && (
        <>
          <div className="flex items-center gap-3">
            <div className="inline-flex shrink-0 rounded-md border p-0.5">
              {modes.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  aria-pressed={state.matchMode === mode.value}
                  disabled={disabled || busy}
                  onClick={() => onChange({ ...state, matchMode: mode.value })}
                  className={cn(
                    'rounded px-3 py-1 text-xs font-medium transition-colors',
                    state.matchMode === mode.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {state.matchMode === 'auto' ? (
                <T context="price list upload auto-match mode description">
                  The AI parser detects the SKU and price columns and reads
                  fixed prices, percentages, or amounts off.
                </T>
              ) : (
                <T context="price list upload pick-columns mode description">
                  Only the two picked columns are read; every row becomes a
                  fixed-price element.
                </T>
              )}
            </p>
          </div>
          {state.matchMode === 'columns' && (
            <WorkbookColumnPicker
              preview={state.preview}
              targets={[
                {
                  key: 'sku',
                  label: (
                    <T context="price list upload column picker label">
                      Part Number Column
                    </T>
                  ),
                },
                {
                  key: 'price',
                  label: (
                    <T context="spreadsheet upload column picker label">
                      Price Column
                    </T>
                  ),
                },
              ]}
              value={{ sku: state.skuColumn, price: state.priceColumn }}
              onChange={(key, column) =>
                onChange(
                  key === 'sku'
                    ? { ...state, skuColumn: column }
                    : { ...state, priceColumn: column },
                )
              }
              disabled={disabled || busy}
            />
          )}
        </>
      )}
    </div>
  );
}
